import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Route } from '../types/module-registry';

export interface SettingsConfig {
	group: string; // e.g., "User Administration", "System Configuration"
	title: string; // e.g., "User Management", "Appearance Settings"
	routes: Route[]; // Required - follows standard route conventions
	icon?: IconDefinition;
	description?: string;
	order?: number; // Optional order within group (lower numbers first)
	requiredPermission?: string | string[]; // RBAC action(s) required to see this setting
	workdOnly?: boolean; // Only visible to Workd staff users
}

export interface SettingsGroup {
	name: string;
	settings: SettingsConfig[];
}

class SettingsRegistryClass {
	private settings = new Map<string, SettingsConfig>();
	private listeners = new Set<() => void>();

	register(moduleName: string, settingsConfigs: SettingsConfig[]) {
		if (!settingsConfigs || !Array.isArray(settingsConfigs)) {
			// Warning: Invalid settings configs for module
			return;
		}

		settingsConfigs.forEach((config, index) => {
			if (!config || typeof config !== 'object') {
				// Warning: Invalid setting config for module
				return;
			}

			// Validate that routes property exists and is an array
			if (!config.routes || !Array.isArray(config.routes)) {
				// Warning: Setting is missing routes array
				return;
			}

			const fullKey = `${moduleName}:${index}`;
			this.settings.set(fullKey, config);
		});

		this.notifyListeners();
	}

	unregister(moduleName: string) {
		const keysToDelete: string[] = [];

		for (const [key] of this.settings.entries()) {
			if (key.startsWith(`${moduleName}:`)) {
				keysToDelete.push(key);
			}
		}

		keysToDelete.forEach((key) => this.settings.delete(key));
		this.notifyListeners();
	}

	getAllSettings(): SettingsConfig[] {
		return Array.from(this.settings.values());
	}

	getSettingsByGroup(): SettingsGroup[] {
		const settingsArray = this.getAllSettings();
		const groupMap = new Map<string, SettingsConfig[]>();

		settingsArray.forEach((setting) => {
			if (!groupMap.has(setting.group)) {
				groupMap.set(setting.group, []);
			}
			groupMap.get(setting.group)!.push(setting);
		});

		return Array.from(groupMap.entries())
			.map(([name, settings]) => ({
				name,
				settings: settings.sort((a, b) => {
					// If both have order, sort by order
					if (a.order != null && b.order != null) {
						return a.order - b.order;
					}
					// If only a has order, it comes first
					if (a.order != null) {
						return -1;
					}
					// If only b has order, it comes first
					if (b.order != null) {
						return 1;
					}
					// If neither has order, sort alphabetically by title
					return a.title.localeCompare(b.title);
				}),
			}))
			.sort((a, b) => a.name.localeCompare(b.name));
	}

	getSetting(title: string): SettingsConfig | undefined {
		for (const [, config] of this.settings.entries()) {
			if (config.title === title) {
				return config;
			}
		}
		return undefined;
	}

	// Generate all routes for settings to be used as children of /settings route
	getAllRoutesForSettings(): Route[] {
		const routes: Route[] = [];

		this.getAllSettings().forEach((setting) => {
			// Check if setting has routes array (new format)
			if (setting.routes && Array.isArray(setting.routes)) {
				setting.routes.forEach((route) => {
					routes.push({
						...route,
						label: route.label || setting.title,
						icon: route.icon || setting.icon,
						// Default the activity-log description to "Viewed <Section
						// Title>" so /settings/<section> pages don't all collapse to
						// the parent "Viewed Settings" via inheritance. Individual
						// routes can still override by declaring their own
						// activityDescription explicitly.
						activityDescription:
							route.activityDescription ?? `Viewed ${setting.title}`,
					});
				});
			}
		});

		return routes;
	}

	subscribe(listener: () => void) {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	}

	private notifyListeners() {
		this.listeners.forEach((listener) => listener());
	}
}

export const SettingsRegistry = new SettingsRegistryClass();
